// The reminders block on the day: a card above the schedules, capped at two
// rows, with the full list one tap away behind its footer.
//
// A BLOCK AND NOT A TAB, which is the design's own decision and worth not
// re-litigating (prototype-v2.md section 1.2): a reminder is by definition the
// thing with no home that gets forgotten, and filing it behind a tab is the
// mechanism by which it gets forgotten. Tabs also grow badges, and a tab
// carrying a red overdue count is exactly the nagging pattern the market
// research rejects.
//
// Three things about it read as arbitrary and are not:
//
//   IT COUNTS IN A DIFFERENT GRAMMAR. Every schedule card says `2/4`; this one
//   says `5 left`. Reminders are deliberately not in the day's figure, and a
//   different sentence is how a user knows that without being told
//   (`leftLabel`, and section 1.3 for the argument).
//
//   IT HAS NO HUE. `--c` is never set on it, so it draws in ink. Colour
//   identifies a schedule, and the absence of colour identifies the thing that
//   belongs to none.
//
//   IT IS ABSENT WHEN THERE IS NOTHING TO SAY, rather than drawing an empty
//   card - and on a FUTURE day it is always absent, because the payload carries
//   no reminders for a day that has not arrived (api/src/domain/day.ts).

import { Icon } from '../day/Icon.tsx';
import type { IsoDay } from '../api/client.ts';
import { ReminderRow } from './ReminderRow.tsx';
import { leftLabel, openCount, remindersLine, remindersOnDay } from './reminders-model.ts';
import { useVoice } from '../shell/voice.ts';
import type { ReminderView } from './reminders-model.ts';

interface Props {
  /** Every reminder open on the day being looked at, in the server's order. */
  reminders: readonly ReminderView[];
  /** The account's today: every date on a row is relative to it. */
  today: IsoDay;
  /**
   * What has been ticked in THIS sitting, which stays on screen struck through
   * so the tick is reversible instead of the row vanishing under the finger
   * that ticked it. Held by whoever owns the day, and emptied when the day
   * changes - a pin belongs to a sitting on one day, not to the reminder.
   */
  pinned: ReadonlySet<string>;
  onToggle: (id: string) => void;
  /** Opens one reminder's edit sheet from its text. */
  onEdit: (id: string) => void;
  /** Opens the full list. */
  onOpenAll: () => void;
}

export function RemindersCard({ reminders, today, pinned, onToggle, onEdit, onOpenAll }: Props) {
  // BEFORE the early return below, because a hook cannot sit after one: the
  // guard is on a prop, so a card that gained its first reminder would render a
  // different number of hooks than the one that had none.
  const voice = useVoice();

  if (reminders.length === 0) return null;

  const { rows, hidden } = remindersOnDay(reminders, pinned);
  const open = openCount(reminders);
  // The count in the head above is plain in both voices; this line is not.
  const line = remindersLine(open, voice);

  return (
    <section className="card rem">
      <div className="card-head">
        {/* The head is the way into the full list, exactly as a schedule card's
            head is the way into its sheet. `h2` under the day's `h1`, so the
            heading order has no gap in it. */}
        <h2 className="htitle">
          <button className="hbtn" onClick={onOpenAll}>
            <span className="chip">
              <Icon name="pin" />
            </span>
            <span className="hname">Reminders</span>
          </button>
        </h2>
        <span className="card-count">{leftLabel(open)}</span>
      </div>

      <div className="card-body">
        <div>
          <ul>
            {rows.map((reminder) => (
              <ReminderRow
                key={reminder.id}
                reminder={reminder}
                today={today}
                onToggle={onToggle}
                onEdit={onEdit}
              />
            ))}
          </ul>
        </div>
      </div>

      {line !== '' && <div className="remnote">{line}</div>}

      {/* THE FOOTER IS WHY THE CAP IS SAFE. The block shows two; this says how
          many it is not showing and goes to all of them. A capped list with no
          way to the rest is the trap this client has met five times - an
          interface that shows less than there is and points nowhere. */}
      <button className="remfoot" onClick={onOpenAll}>
        <span>All reminders</span>
        <span className="n tnum">{hidden > 0 ? `${hidden} more` : ''}</span>
        <Icon name="chevR" />
      </button>
    </section>
  );
}
